<script setup lang="ts">
    import { ref, computed, onMounted, watch, nextTick } from 'vue';
    import SyncConfigOptions from '@/js/back-office/components/SyncConfigOptions.vue';
    import { useRouter } from 'vue-router';
    import EchoButton from '@/js/components/EchoButton.vue';
    import SettingsPageLayout from '@/js/components/settings/SettingsPageLayout.vue';
    import InputLabel from '@/js/components/InputLabel.vue';
    import TextInput, { TextInputWidth } from '@/js/components/TextInput.vue';
    import AutoComplete from '@/js/components/autoComplete/AutoComplete.vue';
    import type { Option } from '@/js/components/autoComplete/types';
    import { __ } from '@/js/stores/expressions';
    import { useSuccessDialog } from '@/js/stores/success-dialog';
    import { useElementFocus } from '@/js/composables/useElementFocus';
    import {
        useMutationMigrateCompany,
        useQueryGetUnmigratedCompanies,
        useQueryGetMigrateTenantConfigOptions,
    } from '@/js/services/api/backOffice/backOfficeQueries';
    import FormGroup from '@/js/components/FormGroup.vue';
    import { CheckIcon, XIcon } from '@lucide/vue';

    const router = useRouter();
    const successDialogStore = useSuccessDialog();
    const { setFocus } = useElementFocus();

    const { mutateAsync: migrateCompanyMutation, isPending: migrateCompanyIsPending } =
        useMutationMigrateCompany();

    const { data: companiesData } = useQueryGetUnmigratedCompanies();
    const companies = computed(() => companiesData.value ?? []);

    const { data: configOptionsData } = useQueryGetMigrateTenantConfigOptions();
    const configOptions = computed(() => configOptionsData.value ?? []);

    const selectedConfigs = ref<string[]>([]);

    const stopWatch = watch(
        configOptionsData,
        (newOptions) => {
            if (newOptions) {
                selectedConfigs.value = newOptions.filter((o) => o.isSelected).map((o) => o.name);
                nextTick(() => stopWatch());
            }
        },
        { immediate: true }
    );

    const form = ref<{ companyId: number | ''; name: string }>({
        companyId: '',
        name: '',
    });

    const canSubmit = computed(() => !!form.value.name.trim() && form.value.companyId !== '');

    const companySearch = ref<string | null>(null);

    const companyOptions = computed<Option[]>(() =>
        companies.value.map((c) => ({ name: c.name, value: String(c.id) }))
    );

    const onCompanySelect = (option: Option) => {
        form.value.companyId = Number(option.value);
    };

    const onCompanyClear = () => {
        form.value.companyId = '';
    };

    const submit = async () => {
        if (!canSubmit.value) return;

        const name = form.value.name.trim();
        const companyId = form.value.companyId as number;

        await migrateCompanyMutation({
            name,
            companyId,
            configs: selectedConfigs.value.length > 0 ? selectedConfigs.value : undefined,
        });

        successDialogStore.openDialog({
            message: __('back-office-install-customer-success', { name }),
        });

        router.push('/back-office/migrate-legacy-integration');
    };

    const cancel = () => {
        router.push('/back-office/overview');
    };

    onMounted(() => {
        setFocus('#legacyCompany');
    });
</script>
<template>
    <SettingsPageLayout>
        <template #header>
            <h1 class="heading-lg-light">
                {{ __('back-office-install-customer-from-v1') }}
            </h1>
            <p class="text-body">
                {{ __('back-office-install-customer-subtitle') }}
            </p>
        </template>
        <template #topbar>
            <EchoButton
                variant="primary"
                :disabled="!canSubmit"
                :loading="migrateCompanyIsPending"
                @click="submit"
            >
                {{ __('save') }}
                <template #icon-right><CheckIcon /></template>
            </EchoButton>
            <EchoButton variant="secondary" @click="cancel">
                {{ __('cancel') }}
                <template #icon-right><XIcon /></template>
            </EchoButton>
        </template>
        <template #content>
            <form class="w-lg max-w-full" @submit.prevent="submit">
                <FormGroup>
                    <AutoComplete
                        v-model="companySearch"
                        input-id="legacyCompany"
                        :label="__('back-office-install-customer-select-company')"
                        :options="companyOptions"
                        @select="onCompanySelect"
                        @clear="onCompanyClear"
                    />
                    <div>
                        <InputLabel for="tenantName">
                            {{ __('back-office-install-customer-name') }}
                        </InputLabel>
                        <TextInput
                            id="tenantName"
                            v-model="form.name"
                            :width="TextInputWidth.Full"
                        />
                    </div>
                    <SyncConfigOptions v-model="selectedConfigs" :options="configOptions" />
                </FormGroup>
            </form>
        </template>
    </SettingsPageLayout>
</template>
